import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import '@/App.css';

// Components
import LandingPage, { SignupPage, LoginPage } from './components/LandingPage';
import AuthCallback from './components/AuthCallback';
import ProtectedRoute from './components/ProtectedRoute';
import Navigation from './components/Navigation';
import PinLockScreen from './components/PinLockScreen';
import MasterLockScreen from './components/MasterLockScreen';
import JournalPage from './components/JournalPage';
import StoriesPage from './components/StoriesPage';
import TopicPage from './components/TopicPage';
import TimelinePage from './components/TimelinePage';
import ActivityPage from './components/ActivityPage';
import EditEntryPage from './components/EditEntryPage';
import SettingsPage from './components/SettingsPage';
import InvitePage from './components/InvitePage';
import StoryInvitePage from './components/StoryInvitePage';
import QRJoinPage from './components/QRJoinPage';
import TermsPage from './components/TermsPage';
import { RecordingProvider, useRecordingContext } from './contexts/RecordingContext';
import { NavigationProvider, useNavigationDirection } from './contexts/NavigationContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios - use token from localStorage for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('noyo_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Swipe Navigation hook
function useSwipeNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const { isRecording } = useRecordingContext();
  const { setNavigationDirection } = useNavigationDirection();

  const pages = ['/', '/stories', '/timeline', '/activity'];

  // Swipe threshold - increased from 80 to 150 for less sensitivity
  const SWIPE_THRESHOLD = 150;

  useEffect(() => {
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      // Swipe navigation disabled
      return;

      touchEndX.current = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX.current;
      const currentIndex = pages.indexOf(location.pathname);

      if (currentIndex === -1) return;
      if (Math.abs(diff) < SWIPE_THRESHOLD) return;

      if (diff > 0 && currentIndex < pages.length - 1) {
        // Swiped left, going forward (to the right page)
        setNavigationDirection(1);
        navigate(pages[currentIndex + 1]);
      } else if (diff < 0 && currentIndex > 0) {
        // Swiped right, going backward (to the left page)
        setNavigationDirection(-1);
        navigate(pages[currentIndex - 1]);
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [location.pathname, navigate, isRecording, setNavigationDirection]);
}

// ScrollToTop component — scrolls to top on every route change (path or search params)
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname, search]);
  return null;
}

function AppRouter() {
  const [user, setUser] = useState(null);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('noyo_dark_mode') === 'true');
  const [pinLocked, setPinLocked] = useState(false);
  const [pinEnabled, setPinEnabled] = useState(false);
  const [initialPinCheckDone, setInitialPinCheckDone] = useState(false);
  const [masterLocked, setMasterLocked] = useState(false);
  const [masterLockChecked, setMasterLockChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useSwipeNavigation();

  // Check master lock status on app load
  useEffect(() => {
    const checkMasterLock = async () => {
      // If already unlocked in this session, skip
      if (sessionStorage.getItem('noyo_master_unlocked') === 'true') {
        setMasterLockChecked(true);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/master-lock/status`);
        if (res.data.locked) {
          setMasterLocked(true);
        }
      } catch (e) {
        // If endpoint fails, assume not locked
      }
      setMasterLockChecked(true);
    };
    checkMasterLock();
  }, []);

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('noyo_dark_mode', darkMode);
  }, [darkMode]);

  // Apply pretty theme if saved
  useEffect(() => {
    if (localStorage.getItem('noyo_pretty') === 'true') {
      document.documentElement.classList.add('pretty');
    }
    if (localStorage.getItem('noyo_sunset') === 'true') {
      document.documentElement.classList.add('sunset');
    }
    if (localStorage.getItem('noyo_twilight') === 'true') {
      document.documentElement.classList.add('twilight');
    }
  }, []);

  // Remember last visited page
  useEffect(() => {
    const mainPages = ['/', '/stories', '/timeline', '/activity'];
    if (mainPages.includes(location.pathname)) {
      localStorage.setItem('noyo_last_page', location.pathname);
    }
  }, [location.pathname]);

  // PIN lock on tab visibility change
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden && pinEnabled) {
        setPinLocked(true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [pinEnabled]);

  const handleThemeChange = (isDark) => {
    setDarkMode(isDark);
  };

  const handleUserLoaded = (userData) => {
    setUser(userData);
    axios.get(`${API}/settings`).then(res => {
      const s = res.data;
      setPinEnabled(s.pin_enabled);
      
      // Show PIN lock on initial app load if PIN is enabled
      // Only do this once per session (check sessionStorage)
      if (s.pin_enabled && !sessionStorage.getItem('noyo_pin_verified')) {
        setPinLocked(true);
      }
      setInitialPinCheckDone(true);
      
      // Only update dark mode from server if there's no local override
      // This prevents theme flashing on page load
      const localDarkMode = localStorage.getItem('noyo_dark_mode');
      if (localDarkMode === null && s.dark_mode !== undefined) {
        setDarkMode(s.dark_mode);
      }
    }).catch(() => {
      setInitialPinCheckDone(true);
    });
  };

  // Handle successful PIN unlock
  const handlePinUnlock = () => {
    setPinLocked(false);
    sessionStorage.setItem('noyo_pin_verified', 'true');
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('noyo_token');
    setUser(null);
    navigate('/welcome');
  };

  // Check for Google OAuth callback with authorization code
  const hasAuthCode = location.pathname.includes('/auth/google/callback') && location.search?.includes('code=');
  if (hasAuthCode) {
    return <AuthCallback />;
  }

  // Show loading while checking master lock
  if (!masterLockChecked) {
    return (
      <div className="fixed inset-0 bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show master lock screen if app is locked
  if (masterLocked) {
    return <MasterLockScreen onUnlock={() => setMasterLocked(false)} />;
  }

  const isEditPage = location.pathname.includes('/entry/') && location.pathname.includes('/edit');

  return (
    <>
      {pinLocked && <PinLockScreen onUnlock={() => setPinLocked(false)} />}
      <ScrollToTop />
      {user && !isEditPage && <Navigation user={user} onLogout={handleLogout} />}
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/google/callback" element={<AuthCallback />} />
          <Route path="/invite/:linkId" element={<InvitePage user={user} />} />
          <Route path="/story-invite/:linkId" element={<StoryInvitePage user={user} />} />
          <Route path="/join/:folderId" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><QRJoinPage /></ProtectedRoute>} />
          <Route path="/" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><JournalPage /></ProtectedRoute>} />
          <Route path="/journal" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><JournalPage /></ProtectedRoute>} />
          <Route path="/entry/:entryId/edit" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><EditEntryPage /></ProtectedRoute>} />
          <Route path="/stories" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><StoriesPage /></ProtectedRoute>} />
          <Route path="/stories/shared/:folderId" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><TopicPage /></ProtectedRoute>} />
          <Route path="/stories/:topicId" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><TopicPage /></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><TimelinePage /></ProtectedRoute>} />
          <Route path="/activity" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><ActivityPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute onUserLoaded={handleUserLoaded}><SettingsPage onThemeChange={handleThemeChange} onLogout={handleLogout} /></ProtectedRoute>} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
      </AnimatePresence>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <RecordingProvider>
          <NavigationProvider>
            <AppRouter />
          </NavigationProvider>
        </RecordingProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
